import { forwardRef, useEffect, useRef } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import "./Checkbox.css";

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Visible label. Omit it only when an `aria-label` is supplied instead. */
  label?: ReactNode;
  /**
   * Renders the mixed state and sets the DOM `indeterminate` flag, which has
   * no HTML attribute and must be assigned imperatively.
   */
  indeterminate?: boolean;
  /**
   * Higher-contrast treatment for use on top of a photograph: white ring on a
   * scrim so it reads against both a white and a black image.
   */
  onImage?: boolean;
}

/**
 * A native `<input type="checkbox">` kept in the accessibility tree and in the
 * form, with the visual box drawn alongside it. `className` and `...rest` land
 * on the input; the wrapper takes `wrapperClassName`-free defaults.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox(
    {
      label,
      indeterminate = false,
      onImage = false,
      className,
      disabled,
      ...rest
    },
    ref,
  ) {
    const innerRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (innerRef.current !== null) {
        innerRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const setRefs = (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref !== null) {
        ref.current = node;
      }
    };

    const wrapperClasses = [
      "fc-checkbox",
      onImage ? "fc-checkbox--on-image" : null,
      indeterminate ? "is-indeterminate" : null,
      disabled === true ? "is-disabled" : null,
    ]
      .filter((value): value is string => Boolean(value))
      .join(" ");

    const inputClasses = ["fc-checkbox__input", className]
      .filter((value): value is string => Boolean(value))
      .join(" ");

    return (
      <label className={wrapperClasses}>
        <input
          {...rest}
          ref={setRefs}
          type="checkbox"
          disabled={disabled}
          className={inputClasses}
          aria-checked={indeterminate ? "mixed" : undefined}
        />
        <span className="fc-checkbox__box" aria-hidden="true">
          <svg
            className="fc-checkbox__tick"
            viewBox="0 0 12 12"
            width="12"
            height="12"
            focusable="false"
          >
            <path
              d="M2.5 6.2 4.9 8.6 9.5 3.6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="fc-checkbox__dash" />
        </span>
        {label !== undefined && label !== null ? (
          <span className="fc-checkbox__label">{label}</span>
        ) : null}
      </label>
    );
  },
);
